import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/common/Layout';
import { solicitudesService } from '../services/solicitudes';
import toast from 'react-hot-toast';
import { AlertTriangle, Download, RefreshCw } from 'lucide-react';

const ESTADO_LABEL = {
    pendiente: 'Pendiente',
    en_ejecucion: 'En ejecución',
    finalizada: 'Finalizada',
};

const ESTADO_COLOR = {
    pendiente: 'bg-yellow-100 text-yellow-800',
    en_ejecucion: 'bg-blue-100 text-blue-800',
    finalizada: 'bg-green-100 text-green-800',
};

const PRIORIDAD_COLOR = {
    urgente: 'bg-purple-100 text-purple-800',
    alta: 'bg-red-100 text-red-800',
    media: 'bg-yellow-100 text-yellow-800',
    baja: 'bg-green-100 text-green-800',
};

const ObservacionesPage = () => {
    const [observaciones, setObservaciones] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [descargando, setDescargando] = useState(false);

    const cargar = useCallback(async () => {
        setCargando(true);
        try {
            const data = await solicitudesService.getAll({ limit: 500, soloHallazgos: true });
            setObservaciones(data);
        } catch {
            toast.error('Error al cargar las observaciones');
        } finally {
            setCargando(false);
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const handleDescargarExcel = async () => {
        setDescargando(true);
        try {
            await solicitudesService.exportarExcel(true);
            toast.success('Excel descargado');
        } catch {
            toast.error('Error al descargar el Excel');
        } finally {
            setDescargando(false);
        }
    };

    return (
        <Layout>
            <div className="p-6">
                <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                            <AlertTriangle className="h-7 w-7 mr-2 text-amber-600" />
                            Observaciones
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">
                            Hallazgos generados automáticamente desde los checklists de inspección
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={cargar}
                            disabled={cargando}
                            className="flex items-center px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-semibold text-sm shadow-sm disabled:opacity-60"
                        >
                            <RefreshCw className={`h-4 w-4 mr-2 ${cargando ? 'animate-spin' : ''}`} />
                            Actualizar
                        </button>
                        <button
                            onClick={handleDescargarExcel}
                            disabled={descargando}
                            className="flex items-center px-4 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-60 transition-colors font-semibold text-sm shadow-sm"
                        >
                            {descargando
                                ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                : <Download className="h-4 w-4 mr-2" />
                            }
                            {descargando ? 'Descargando...' : 'Descargar Excel'}
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Código</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Descripción</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">CT</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Prioridad</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Asignado a</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Fecha</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {!cargando && observaciones.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="px-4 py-14 text-center text-gray-400">
                                            No hay observaciones registradas todavía.
                                        </td>
                                    </tr>
                                )}
                                {observaciones.map((obs) => (
                                    <tr key={obs.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{obs.codigo}</td>
                                        <td className="px-4 py-3 text-sm text-gray-700 max-w-md">{obs.descripcion}</td>
                                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{obs.ubicacion || '—'}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${ESTADO_COLOR[obs.estado] || 'bg-gray-100 text-gray-700'}`}>
                                                {ESTADO_LABEL[obs.estado] || obs.estado}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            {obs.prioridad && (
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${PRIORIDAD_COLOR[obs.prioridad] || 'bg-gray-100 text-gray-700'}`}>
                                                    {obs.prioridad.charAt(0).toUpperCase() + obs.prioridad.slice(1)}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                                            {obs.asignado_a_nombre || obs.asignado_a_email || 'Sin asignar'}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                                            {obs.fecha_creacion ? new Date(obs.fecha_creacion).toLocaleDateString('es-PE') : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default ObservacionesPage;
